// Local (IndexedDB) backup of in-progress recordings.
//
// While recording, downsampled 16-bit PCM chunks are appended here every few
// seconds so a page refresh / crash mid-recording does not lose the audio.
// Raw PCM is used (instead of MediaRecorder output) so an interrupted
// recording can be listened to, CONTINUED seamlessly (new chunks are appended
// to the same session), and assembled into one valid WAV file for upload.
// The backup is deleted only after the upload to the backend succeeds.
//
// Every function here swallows storage errors: the backup must never break
// the primary recording flow.

const DB_NAME = "documd-recording-backup";
const DB_VERSION = 1;
const SESSIONS_STORE = "sessions";
const CHUNKS_STORE = "chunks";

export const BACKUP_SAMPLE_RATE = 16000;

export interface BackupSessionMeta {
  id: string;
  startedAt: number;
  patientName: string;
  patientId?: string;
  templateId?: number;
  visitType?: string;
  pronouns?: string;
  noteLength?: string;
  sampleRate: number;
}

export interface PendingBackup {
  meta: BackupSessionMeta;
  blob: Blob; // playable/uploadable WAV assembled from the saved chunks
  approxSeconds: number;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB not available"));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(SESSIONS_STORE)) {
        db.createObjectStore(SESSIONS_STORE, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(CHUNKS_STORE)) {
        const chunks = db.createObjectStore(CHUNKS_STORE, {
          keyPath: ["sessionId", "seq"],
        });
        chunks.createIndex("bySession", "sessionId");
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

function reqResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// Downsample a Float32 capture buffer to 16-bit PCM at the target rate
// (averaging the samples in each window to avoid aliasing).
export function downsampleToInt16Pcm(
  input: Float32Array,
  fromRate: number,
  toRate: number = BACKUP_SAMPLE_RATE
): Int16Array {
  const ratio = fromRate / toRate;
  const outLength =
    ratio <= 1 ? input.length : Math.floor(input.length / ratio);
  const out = new Int16Array(outLength);
  for (let i = 0; i < outLength; i++) {
    let sample: number;
    if (ratio <= 1) {
      sample = input[i];
    } else {
      const start = Math.floor(i * ratio);
      const end = Math.min(Math.floor((i + 1) * ratio), input.length);
      let sum = 0;
      for (let j = start; j < end; j++) sum += input[j];
      sample = end > start ? sum / (end - start) : 0;
    }
    const clamped = Math.max(-1, Math.min(1, sample));
    out[i] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
  }
  return out;
}

function writeString(view: DataView, offset: number, text: string) {
  for (let i = 0; i < text.length; i++) {
    view.setUint8(offset + i, text.charCodeAt(i));
  }
}

// Wrap raw 16-bit mono PCM buffers in a standard WAV header.
export function encodeWavFromPcm(
  pcmBuffers: ArrayBuffer[],
  sampleRate: number
): Blob {
  const dataBytes = pcmBuffers.reduce((n, b) => n + b.byteLength, 0);
  const header = new ArrayBuffer(44);
  const view = new DataView(header);
  writeString(view, 0, "RIFF");
  view.setUint32(4, 36 + dataBytes, true);
  writeString(view, 8, "WAVE");
  writeString(view, 12, "fmt ");
  view.setUint32(16, 16, true); // fmt chunk size
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true); // byte rate
  view.setUint16(32, 2, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  writeString(view, 36, "data");
  view.setUint32(40, dataBytes, true);
  return new Blob([header, ...pcmBuffers], { type: "audio/wav" });
}

export async function createBackupSession(
  meta: Omit<BackupSessionMeta, "id" | "startedAt">
): Promise<string | null> {
  try {
    const db = await openDb();
    const id = `rec-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const tx = db.transaction(SESSIONS_STORE, "readwrite");
    tx.objectStore(SESSIONS_STORE).put({ ...meta, id, startedAt: Date.now() });
    await txDone(tx);
    db.close();
    return id;
  } catch (error) {
    console.warn("Recording backup: failed to create session", error);
    return null;
  }
}

export async function appendBackupChunk(
  sessionId: string,
  seq: number,
  pcm: ArrayBuffer
): Promise<void> {
  try {
    const db = await openDb();
    const tx = db.transaction(CHUNKS_STORE, "readwrite");
    tx.objectStore(CHUNKS_STORE).put({ sessionId, seq, data: pcm });
    await txDone(tx);
    db.close();
  } catch (error) {
    console.warn("Recording backup: failed to save chunk", error);
  }
}

export async function deleteBackupSession(sessionId: string): Promise<void> {
  try {
    const db = await openDb();
    const tx = db.transaction([SESSIONS_STORE, CHUNKS_STORE], "readwrite");
    tx.objectStore(SESSIONS_STORE).delete(sessionId);
    const index = tx.objectStore(CHUNKS_STORE).index("bySession");
    const cursorReq = index.openCursor(IDBKeyRange.only(sessionId));
    cursorReq.onsuccess = () => {
      const cursor = cursorReq.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      }
    };
    await txDone(tx);
    db.close();
  } catch (error) {
    console.warn("Recording backup: failed to delete session", error);
  }
}

async function readSessionPcm(
  db: IDBDatabase,
  sessionId: string
): Promise<ArrayBuffer[] | null> {
  const tx = db.transaction(CHUNKS_STORE, "readonly");
  const chunks = (await reqResult(
    tx.objectStore(CHUNKS_STORE).index("bySession").getAll(sessionId)
  )) as { sessionId: string; seq: number; data: unknown }[];
  await txDone(tx);
  if (chunks.length === 0) return null;
  // Sessions written by an incompatible (older) format are unusable
  if (chunks.some((c) => !(c.data instanceof ArrayBuffer))) return null;
  chunks.sort((a, b) => a.seq - b.seq);
  return chunks.map((c) => c.data as ArrayBuffer);
}

// Assemble the full WAV for a session (used when a continued recording is
// uploaded: old + newly appended chunks become one file).
export async function buildWavFromSession(
  sessionId: string
): Promise<Blob | null> {
  try {
    const db = await openDb();
    const sessionTx = db.transaction(SESSIONS_STORE, "readonly");
    const meta = (await reqResult(
      sessionTx.objectStore(SESSIONS_STORE).get(sessionId)
    )) as BackupSessionMeta | undefined;
    await txDone(sessionTx);
    const pcm = await readSessionPcm(db, sessionId);
    db.close();
    if (!meta || !meta.sampleRate || !pcm) return null;
    return encodeWavFromPcm(pcm, meta.sampleRate);
  } catch (error) {
    console.warn("Recording backup: failed to assemble session audio", error);
    return null;
  }
}

// Returns the most recent session that has audio chunks (i.e. an interrupted
// recording that never finished uploading). Empty or incompatible leftover
// sessions are cleaned up. `excludeId` skips the session currently being
// written.
export async function getLatestPendingBackup(
  excludeId?: string | null
): Promise<PendingBackup | null> {
  try {
    const db = await openDb();
    const tx = db.transaction(SESSIONS_STORE, "readonly");
    const sessions = (await reqResult(
      tx.objectStore(SESSIONS_STORE).getAll()
    )) as BackupSessionMeta[];
    await txDone(tx);

    sessions.sort((a, b) => b.startedAt - a.startedAt);

    for (const meta of sessions) {
      if (excludeId && meta.id === excludeId) continue;
      const pcm = meta.sampleRate ? await readSessionPcm(db, meta.id) : null;
      if (!pcm) {
        void deleteBackupSession(meta.id);
        continue;
      }
      const totalSamples =
        pcm.reduce((n, b) => n + b.byteLength, 0) / 2; // 16-bit samples
      db.close();
      return {
        meta,
        blob: encodeWavFromPcm(pcm, meta.sampleRate),
        approxSeconds: Math.round(totalSamples / meta.sampleRate),
      };
    }

    db.close();
    return null;
  } catch (error) {
    console.warn("Recording backup: failed to read pending backup", error);
    return null;
  }
}
